/**
 * Filter helper utilities for NewEntityFilters component
 * Extracted from the main component to improve modularity and testability
 */
import { ACTIVITIES, OPPORTUNITIES } from 'constants/Entities';

/**
 * Gets fixed visible filters based on entity type
 * @param {Object} entity - Entity configuration
 * @returns {Array} Array of fixed visible filter IDs
 */
export const getFixedVisibleFilters = (entity) => {
    let visible = [];
    if (entity.trueName === ACTIVITIES.trueName) {
        visible.push('isMarketingEmail');
    }
    return visible;
};

/**
 * Applies quick filters overrides based on entity type
 * @param {Array} quickFilters - Array of quick filter IDs
 * @param {Object} entity - Entity configuration
 * @returns {Array} Modified quick filters array
 */
export const applyQuickFiltersOverrides = (quickFilters, entity) => {
    const modifiedFilters = [...quickFilters];
    if (!modifiedFilters.includes('state') && entity.trueName === OPPORTUNITIES.trueName) {
        modifiedFilters.unshift('state');
    }
    return modifiedFilters;
};

/**
 * Gets cached state configuration
 * @param {Object} config - Configuration object
 * @param {Object} config.cache - Cached filter state
 * @param {Array} config.defaultQuickFilters - Default quick filters
 * @param {Array} config.defaultFilters - Default filters
 * @param {Object} config.entity - Entity configuration
 * @returns {Object} Cached state object
 */
export const getStateCached = ({ cache, defaultQuickFilters, defaultFilters, entity }) => {
    let cachedState = null;
    let visibleFilters = [];
    let quickFilters = [];
    let openFilters = {};
    let openQuickFilters = {};
    let crossSchema = {};

    const initialCache = cache;

    if (initialCache) {
        quickFilters = initialCache.hasOwnProperty('quickFilters')
            ? initialCache.quickFilters || []
            : defaultQuickFilters;

        quickFilters = applyQuickFiltersOverrides(quickFilters, entity);

        // All quick filters have to be visible for consistency
        visibleFilters = new Set([
            ...(initialCache.visibleFilters || []),
            ...quickFilters,
            ...getFixedVisibleFilters(entity),
        ]);

        visibleFilters = Array.from(visibleFilters);
        visibleFilters = visibleFilters.filter(
            (filter) => typeof filter === 'string' || filter instanceof String,
        );

        openFilters = cache.openFilters || {};
        openQuickFilters = cache.openQuickFilters || {};
        crossSchema = cache.crossSchema || {};

        cachedState = {
            visibleFilters,
            openFilters,
            openQuickFilters,
            quickFilters,
            crossSchema,
        };
    } else {
        cachedState = {
            visibleFilters: defaultFilters,
            openFilters,
            openQuickFilters,
            quickFilters: defaultQuickFilters,
            crossSchema,
        };
    }

    return cachedState;
};

/**
 * Maps schema array to object with fieldId and fieldConfiguration keys
 * @param {Array} schema - Schema array
 * @returns {Object} Mapped schema object
 */
export const getMappedSchema = (schema) => {
    return schema.reduce((obj, current) => {
        if (!obj.schemaByFieldId) obj.schemaByFieldId = {};
        if (!obj.schemaByFieldConfiguration) obj.schemaByFieldConfiguration = {};
        obj.schemaByFieldId[current.id] = current;
        if (current.fieldConfiguration) {
            obj.schemaByFieldConfiguration[current.fieldConfiguration] = current;
        }
        return obj;
    }, {});
};

/**
 * Gets extra filters schema configuration
 * @param {Object} config - Configuration object
 * @param {Array} config.schema - Filter schema array
 * @param {Array} config.extraSchema - Extra schema configuration
 * @param {Array} config.hiddenFilters - Hidden filters array
 * @param {Object} config.entity - Entity configuration
 * @param {Array<string>} [config.readOnlyFilterIds] - Filter ids locked in add-filters modal
 * @returns {Array} Extra filters schema
 */
export const getExtraFiltersSchema = ({
    schema,
    extraSchema,
    hiddenFilters = [],
    entity,
    readOnlyFilterIds = [],
}) => {
    const options = schema.reduce((filters, filter) => {
        let isStaticFilter = false;

        if (filter.id === 'pendingConversations' && entity === ACTIVITIES.trueName) {
            isStaticFilter = true;
        } else if (filter.id === 'state' && entity === OPPORTUNITIES.trueName) {
            isStaticFilter = true;
        }

        if (!hiddenFilters?.includes(filter.id)) {
            filters.push({
                id: filter.id,
                label: filter.description,
                forceDisabled: isStaticFilter || readOnlyFilterIds.includes(filter.id),
            });
        }
        return filters;
    }, []);

    let groupedOptions = [];

    if (extraSchema && extraSchema.length > 0) {
        const mappedOptions = options.reduce((obj, current) => {
            obj[current.id] = current;
            return obj;
        }, {});

        groupedOptions = extraSchema.reduce((result, current) => {
            let group = {
                label: current.label,
                id: current.label,
            };

            let groupOptions = current.options.reduce((arr, option) => {
                if (mappedOptions[option]) {
                    arr.push(mappedOptions[option]);
                    mappedOptions[option].included = true;
                }
                return arr;
            }, []);

            group.options = groupOptions;
            result.push(group);

            return result;
        }, []);
    }

    let finalOptions = options;
    if (groupedOptions && groupedOptions.length > 0) finalOptions = groupedOptions;

    return finalOptions;
};
